import { useCallback, useMemo } from 'react';
import { useQuery, useMutation } from '@ts-core/lib/apollo';

import { getErrorMessage, useMutationCompletionHandler } from '@ts-core/lib/apollo';

import { getGroupsByCourseQuery } from '../graphql/queries';
import { createGroupMutation, updateGroupMutation, deleteGroupMutation } from '../graphql/mutations';

export default function useGroups({ courseId, onGroupCreated, onGroupUpdated }) {
  const { onError, addItemToCache, removeItemFromCache } = useMutationCompletionHandler();

  /**
   * Get Groups
   */
  const { data, loading, error } = useQuery(getGroupsByCourseQuery, {
    variables: { courseId },
    skip: !courseId,
  });
  const groups = useMemo(() => data?.groups || [], [data]);

  /**
   * Create Group
   */
  const [createGroup, { loading: creatingGroup }] = useMutation(createGroupMutation, {
    onError,
    onCompleted: onGroupCreated,
    update: addItemToCache({
      query: getGroupsByCourseQuery,
      variables: { courseId },
      queryField: 'groups',
      mutationField: 'group',
    }),
  });
  const onCreateGroup = useCallback(
    (input) => {
      createGroup({ variables: { courseId, input } });
    },
    [createGroup, courseId]
  );

  /**
   * Update Group
   */
  const [updateGroup, { loading: updatingGroup }] = useMutation(updateGroupMutation, {
    onError,
    onCompleted: onGroupUpdated,
  });
  const onUpdateGroup = useCallback(
    (groupId, input) => {
      updateGroup({
        variables: { courseId, groupId, input },
      });
    },
    [updateGroup, courseId]
  );

  /**
   * Delete Group
   */
  const [deleteGroup, { loading: deletingGroup }] = useMutation(deleteGroupMutation, {
    onError,
    update: removeItemFromCache({
      query: getGroupsByCourseQuery,
      queryField: 'groups',
      variables: {
        courseId,
      },
      mutationField: 'group',
    }),
  });
  const onDeleteGroup = useCallback(
    (group) => {
      deleteGroup({
        variables: { courseId, groupId: group._id },
        optimisticResponse: {
          group,
        },
      });
    },
    [deleteGroup, courseId]
  );

  return {
    groups,
    loading,
    error: getErrorMessage({ error }),

    onCreateGroup,
    creatingGroup,

    onUpdateGroup,
    updatingGroup,

    onDeleteGroup,
    deletingGroup,
  };
}
